import { Component, computed, EventEmitter, inject, Input, input, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MaterialModule } from '../../../../../shared/material.module';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { selectInactivityStatusForTab } from '../../../../../store/selectors/company-form.selectors';
import { CompanyYearClassification, getInactivityMessage } from '../../../../../utilities/company-activity';
import { getCompanyTabAccessType } from '../../../../../utilities/companyTabAccess';

@Component({
  selector: 'app-tab-toolbar',
  imports: [MaterialModule],
  templateUrl: './tab-toolbar.component.html',
  styleUrl: './tab-toolbar.component.scss'
})
export class TabToolbarComponent {
  private currentUserState = inject(CurrentUserState);
  private store = inject(Store);

  // Signal input so the inactivity selector can switch when tabId changes.
  // (Other inputs stay as legacy @Input for now — no behavior depends on their reactivity.)
  tabId = input<string | null>(null);

  // Tab id used only to resolve company-tab access. Defaults to tabId, which is what almost every
  // tab wants; details and process set it explicitly because they deliberately opt out of the
  // inactivity classification that tabId also drives (details is where inactive periods are edited,
  // so it must stay saveable on an inactive year).
  accessTabId = input<string | null>(null);

  @Input() selectedYear: string = this.currentUserState.defaultMeasurementYear();
  @Input() saveDisabled: boolean = true;
  @Input() showOtherMeasurement: boolean = false;
  @Input() otherMeasurement: string = '';
  @Input() showSave: boolean = true;
  @Input() showKeywordsToggle: boolean = false;
  @Input() keywordsVisible: boolean = false;
  @Output() yearChange = new EventEmitter<string>();
  @Output() otherMeasurementChange = new EventEmitter<string>();
  @Output() keywordsVisibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<void>();

  measurements: string[] = this.currentUserState.accessibleMeasurements();

  // Inactivity classification for this tab, derived from the shared NgRx store.
  // Defaults to 'active' when no tabId is supplied (back-compat for callers not yet updated).
  inactivityClassification = toSignal(
    toObservable(this.tabId).pipe(
      switchMap(tab =>
        tab
          ? this.store.select(selectInactivityStatusForTab(tab))
          : of<CompanyYearClassification>({ status: 'active', firstActiveYear: null })
      )
    ),
    { initialValue: { status: 'active', firstActiveYear: null } as CompanyYearClassification }
  );

  isInactive = computed(() => this.inactivityClassification().status !== 'active');
  inactivityMessage = computed(() => getInactivityMessage(this.inactivityClassification()));

  /**
   * The user holds this tab read-only: Save is hidden here and every control in the tab body is
   * disabled by the tab itself. The backend rejects writes to the tab regardless.
   */
  isReadOnly = computed(() => {
    const accessType = getCompanyTabAccessType(this.accessTabId() ?? this.tabId());
    return accessType !== null && this.currentUserState.isCompanyTabReadOnly(accessType);
  });

  onYearChange(year: string): void {
    this.yearChange.emit(year);
  }

  // mat-select's selectionChange does not fire when the same option is re-selected,
  // so we emit on click to allow re-triggering the load for the current year.
  onYearOptionClick(year: string): void {
    if (year === this.selectedYear) {
      this.yearChange.emit(year);
    }
  }

  // Reload button: re-emits the current year so the tab re-fetches its data,
  // identical to re-selecting the currently displayed year in the dropdown.
  onReload(): void {
    this.yearChange.emit(this.selectedYear);
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurementChange.emit(year);
  }

  onKeywordsToggle(event: MatSlideToggleChange): void {
    this.keywordsVisibleChange.emit(event.checked);
  }
}
